import { Portal } from '@material-ui/core';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { PageLoading } from '../../components/PageLoading/PageLoading';
import { CheckInActiveApi, CompanyHostApi, companyServices, driverCheckInServices, Queue } from '../../domain';
import { useIPNetwork } from '../../hooks/useIPNetwork';
import { useWindowLocationHost } from '../../hooks/useWindowLocationHost';
import webSocketClient from '../../webSocketClient';
import { DriverTVContent } from './components/DriverTVContent';
import styles from './styles.module.css';
import { fetchDriversInAttendance } from './utils';

export type DriverTV = CheckInActiveApi;
export type DriverCalled = CheckInActiveApi;

export type DriverCalledQueue = {
  driverCalled: DriverCalled | null;
  queue: DriverCalled[];
};

export type TV = {
  driverTv: DriverTV;
  inAttendance: boolean;
};

export function DriversTV() {
  const [isWsListenerAdded, setIsWsListenerAdded] = useState(false);
  const [showTV, setShowTV] = useState(false);

  const [drivers, setDrivers] = useState<TV[]>([]);
  const [activeQueues, setActiveQueues] = useState<Queue[]>([]);
  const [driverCalledQueue, setDriverCalledQueue] = useState<DriverCalledQueue>({
    driverCalled: null,
    queue: [],
  });

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<CompanyHostApi | null>(null);

  console.log('🏁 DriversTV - Estado atual driverCalledQueue:', {
    current: driverCalledQueue.driverCalled?.queue?.driver?.name || 'null',
    queueLength: driverCalledQueue.queue.length,
  });

  const driverIp = useIPNetwork();
  const { windowLocationHost } = useWindowLocationHost();

  // const shippingCompanyId = localStorage.getItem('@App:companyId')!;
  // const companyIdIsSame = +shippingCompanyId === company?.id;

  const handleTVData = useCallback(
    async (companyCheckinIps: string, companyId: string) => {
      const driverIpValid = companyCheckinIps.replace(' ', '').split(',').includes(driverIp);

      if (!driverIpValid) return;

      const [[inAttendance, queuesActive], driversCheckin] = await Promise.all([fetchDriversInAttendance(companyId), driverCheckInServices.getDriversCalled(companyId)]);

      // Não define lastDriverCalled aqui para evitar chamadas automáticas
      // Apenas o websocket deve disparar as chamadas de voz

      let formattedDriverList = driversCheckin.filter((driver) => driver.queue);
      setActiveQueues(queuesActive);
      setDrivers(
        formattedDriverList.map((item) => {
          const truckId = item.queue?.truck?.id;
          const isAttendanceDriver = inAttendance.includes(truckId);

          return {
            driverTv: item,
            inAttendance: isAttendanceDriver,
          };
        })
      );
    },
    [driverIp]
  );

  const fetchDataPage = useCallback(async () => {
    if (!driverIp) return;

    setLoading(true);
    try {
      const company = await companyServices.getCompanyByHost(windowLocationHost);
      setCompany(company);

      const companyId = company.id.toString();

      localStorage.setItem('@App:companyId', companyId);

      const [companySettings, companyAttendanceStatus] = await Promise.all([companyServices.getSettings(companyId), companyServices.getAttendanceStatus(companyId)]);

      if (companyAttendanceStatus) {
        setShowTV(companyAttendanceStatus.status);
      }

      await handleTVData(companySettings.checkinIp, companyId);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [driverIp, windowLocationHost, handleTVData]);

  const attendedTotal = activeQueues.length;

  const driversEmpty = useMemo(() => drivers.length === 0, [drivers]);

  // Função para adicionar motoristas à fila
  const addDriverToQueue = useCallback((newDriver: DriverCalled) => {
    setDriverCalledQueue((prev) => {
      // Se não há motorista atual sendo chamado, define como atual
      if (!prev.driverCalled) {
        console.log('🎯 DriversTV - Definindo motorista atual:', newDriver.queue?.driver?.name);
        return {
          driverCalled: newDriver,
          queue: prev.queue,
        };
      }

      // Se já há motorista sendo chamado, adiciona à fila
      console.log('📝 DriversTV - Adicionando à fila:', newDriver.queue?.driver?.name);
      return {
        driverCalled: prev.driverCalled,
        queue: [...prev.queue, newDriver],
      };
    });
  }, []);

  //TODO Requisição websocket para verificar se o motorista foi chamado
  useEffect(() => {
    const handleWebSocketResponse = async (message: { driverCalled: DriverCalled | null }) => {
      console.log('🎯 DriversTV - WebSocket recebido:', message.driverCalled?.queue?.driver?.name);

      if (message.driverCalled && !isWsListenerAdded) {
        setIsWsListenerAdded(true);
      }

      // Adiciona o motorista à fila usando a nova função
      if (message.driverCalled) {
        console.log('📢 DriversTV - Adicionando motorista via WebSocket:', message.driverCalled.queue?.driver?.name);
        addDriverToQueue(message.driverCalled);
      }

      // Depois busca os dados atualizados
      await fetchDataPage();
    };

    webSocketClient.on('checkInCallResponse', handleWebSocketResponse);

    return () => {
      webSocketClient.off('checkInCallResponse', handleWebSocketResponse);
      setIsWsListenerAdded(false);
    };
  }, [fetchDataPage, isWsListenerAdded, addDriverToQueue]);

  useEffect(() => {
    fetchDataPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowLocationHost, driverIp, isWsListenerAdded]);

  return (
    <div className={styles['tv-container']}>
      {(loading || driversEmpty || !showTV) && (
        <Portal>
          <PageLoading loading style={{ backgroundColor: '#ffffff' }} />
        </Portal>
      )}

      <DriverTVContent
        driverCalledQueue={driverCalledQueue}
        setDriverCalledQueue={setDriverCalledQueue}
        attendeds={attendedTotal}
        tvList={drivers}
        company={company}
        refetchDriverList={fetchDataPage}
      />
    </div>
  );
}
